// src/settings/sections/BillingSection.jsx
import React, { useMemo, useState } from "react";
import { useAuthBootstrap } from "../../../context/AuthBootstrapContext";
import "./BillingSection.css";

const API_BASE = "https://api-2026.fikacreatives.co";

function parseDate(value) {
  if (!value) return null;
  // Normalize Postgres timestamp: "2026-04-25 21:09:38+00" → "2026-04-25T21:09:38+00:00"
  const normalized = String(value)
    .replace(" ", "T")
    .replace(/\+(\d{2})$/, "+$1:00");
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function addDays(value, days) {
  const date = parseDate(value);
  if (!date) return null;
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function getDaysRemaining(target) {
  const end = target instanceof Date ? target : parseDate(target);
  if (!end) return null;

  const now = new Date();
  const diffMs = end.getTime() - now.getTime();
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  return Math.max(days, 0);
}

function formatDate(value) {
  const date = parseDate(value);
  if (!date) return "";
  return date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function getBillingContent({ person, verification }) {
  const personStatus = String(person?.status || "").toUpperCase();
  const subscriptionStatus = String(
    verification?.subscription_status || ""
  ).toLowerCase();
  const isPaymentComplete = personStatus === "PAYMENT_COMPLETE";

  if (
    !isPaymentComplete &&
    (subscriptionStatus === "free" || subscriptionStatus === "trialing")
  ) {
    const trialEnd =
      subscriptionStatus === "trialing"
        ? verification?.current_period_end
        : addDays(person?.created_at, 14);
    const daysLeft = getDaysRemaining(trialEnd);

    if (!daysLeft || daysLeft === 0) {
      return {
        state: "application-expired",
        eyebrow: "Membership access",
        title: "Your free trial has ended",
        body:
          "Get access to curated creative matchmaking, monthly events, and café discounts for $12-$25/month. Cancel anytime.",
        meta: "",
        ctaLabel: "Start membership",
        ctaAction: "pay",
      };
    }

    return {
      state: "application-complete",
      eyebrow: "Membership access",
      title: `${daysLeft} day${daysLeft === 1 ? "" : "s"} left in your free trial`,
      body:
        "Get access to curated creative matchmaking, monthly events, and café discounts for $12-$25/month. Cancel anytime.",
      meta: "",
      ctaLabel: "Start membership",
      ctaAction: "pay",
    };
  }

  if (
    personStatus === "PAYMENT_STARTED" ||
    personStatus === "PAYMENT_COMPLETE" ||
    subscriptionStatus === "active" ||
    subscriptionStatus === "past_due" ||
    subscriptionStatus === "canceled" ||
    subscriptionStatus === "incomplete_expired" ||
    subscriptionStatus === "unpaid"
  ) {
    const membershipEnd = verification?.current_period_end;
    const daysLeft = getDaysRemaining(membershipEnd);

    if (daysLeft === 0 && subscriptionStatus !== "active") {
      return {
        state: "membership-expired",
        eyebrow: "Membership",
        title: "Your membership period has ended",
        body:
          "Renew your membership to keep access to events, including coworking and cafe hangouts, coffee discounts, sending coffees, and the Fika community.",
        meta: membershipEnd ? `Ended ${formatDate(membershipEnd)}` : "",
        ctaLabel: "Manage membership",
      };
    }

    return {
      state: "payment-complete",
      eyebrow: "Membership",
      title:
        daysLeft === 1
          ? "1 day left in your trial period"
          : `Monthly member`,
      body:
        "You have access to events, including coworking and cafe hangouts, coffee discounts, sending coffees, and the Fika community. To make any changes, email hello@fikacreatives.co",
      meta: membershipEnd ? `Ends ${formatDate(membershipEnd)}` : "",
      ctaLabel: "Manage membership",
    };
  }

  return {
    state: "unknown",
    eyebrow: "Membership",
    title: "Billing details unavailable",
    body: "We couldn’t determine your membership state yet.",
    meta: "",
    ctaLabel: null,
  };
}

export default function BillingSection() {
  const { person, verification } = useAuthBootstrap();
  const [redirecting, setRedirecting] = useState(false);
  const [portalRedirecting, setPortalRedirecting] = useState(false);

  const content = useMemo(() => {
    return getBillingContent({ person, verification });
  }, [person, verification]);

  const isNYC = (person?.city || "").toLowerCase().includes("new york");
  const subscriptionStatus = String(verification?.subscription_status || "").toLowerCase();
  const showNYCTrialNotice =
    isNYC &&
    (subscriptionStatus === "free" || subscriptionStatus === "trialing");

  async function goToPortal() {
    setPortalRedirecting(true);
    try {
      const res = await fetch(`${API_BASE}/api/me/billing-portal`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setPortalRedirecting(false);
      }
    } catch {
      setPortalRedirecting(false);
    }
  }

  async function goToPayment() {
    setRedirecting(true);
    try {
      let sessionId = person?.apply_session_id || null;

      if (!sessionId) {
        const res = await fetch(`${API_BASE}/api/me/apply-session`, { credentials: "include" });
        const data = await res.json();
        sessionId = data?.apply_session_id || null;
      }

      const params = new URLSearchParams({ no_trial: "true" });
      if (sessionId) params.set("apply_session_id", sessionId);
      window.location.href = `https://www.fikacreatives.co/apply/pay?${params.toString()}`;
    } catch {
      setRedirecting(false);
    }
  }

  return (
    <section className={`billingSection billingSection--${content.state}`}>
      {showNYCTrialNotice && (
        <div className="billingNotice">
          Fika has updated its trial — your free access has been extended to a full first month.
        </div>
      )}
      <div className="billingCard">
        <div className="billingEyebrow">{content.eyebrow}</div>
        <h3 className="billingTitle">{content.title}</h3>
        <p className="billingBody">{content.body}</p>

        {content.meta ? <p className="billingMeta">{content.meta}</p> : null}

        {content.ctaAction === "pay" && content.ctaLabel && (
          <button type="button" className="billingButton" onClick={goToPayment} disabled={redirecting}>
            {redirecting ? "Loading…" : content.ctaLabel}
          </button>
        )}
        {content.ctaAction !== "pay" && content.ctaLabel && (
          <button type="button" className="billingButton" onClick={goToPortal} disabled={portalRedirecting}>
            {portalRedirecting ? "Loading…" : content.ctaLabel}
          </button>
        )}
      </div>
    </section>
  );
}